import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { BookOpen, RefreshCw, AlertCircle, MessageSquare, ExternalLink } from 'lucide-react'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { AppHeader } from '../components'
import { fetchCoachingNotes, type CoachingNote } from '../services/albatross'
import { useActiveCompetition } from '../hooks/useActiveCompetition'
import { useAuth } from '../hooks/useAuth'
import { config } from '../config/environment'

const ALBATROSS_BASE = (config.albatrossUrl || 'https://albatross.dev.flywideopen.com').replace(/\/api\/?$/, '')

export default function CoachingNotesPage() {
  const { user } = useAuth()
  const { albatrossCompId } = useActiveCompetition()
  const [notes, setNotes] = useState<CoachingNote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const pilotIdentifier = user
    ? user.participantNumber || `${user.firstName} ${user.lastName}`
    : ''

  useEffect(() => {
    if (!albatrossCompId || !pilotIdentifier) {
      setLoading(false)
      if (!albatrossCompId) setError('No active competition found.')
      return
    }

    setLoading(true)
    setError(null)
    fetchCoachingNotes(albatrossCompId, pilotIdentifier)
      .then(setNotes)
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Failed to load coaching notes'
        if (msg.includes('404')) {
          setError('Coaching notes are not yet available for this competition.')
        } else {
          setError(msg)
        }
      })
      .finally(() => setLoading(false))
  }, [albatrossCompId, pilotIdentifier])

  return (
    <ProtectedRoute>
      <Helmet>
        <title>Coaching Notes - FWO Pilot Portal</title>
        <meta name="description" content="Your personalised coaching notes from Albatross flight analysis" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <AppHeader activePage="coaching-notes" />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-fwo-blue" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Coaching Notes</h2>
              <p className="text-gray-600 text-sm">
                Your personalised flight analysis and coaching feedback from Albatross
              </p>
            </div>
          </div>

          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="animate-spin inline-block mb-3">
                <RefreshCw className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-gray-500 text-sm">Loading coaching notes…</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && notes.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium mb-1">No coaching notes yet</p>
              <p className="text-gray-400 text-sm">
                Coaching notes will appear here once your coach has reviewed your flights.
              </p>
            </div>
          )}

          {!loading && !error && notes.length > 0 && (
            <div className="space-y-4">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-5"
                >
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div>
                      {note.taskName && (
                        <p className="font-semibold text-gray-900">{note.taskName}</p>
                      )}
                      {note.date && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(note.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {note.rank !== undefined && note.totalPilots !== undefined && (
                        <span className="text-xs font-medium text-gray-700 bg-gray-100 rounded-full px-3 py-1">
                          {note.rank} / {note.totalPilots}
                        </span>
                      )}
                      {note.madeGoal !== undefined && (
                        <span className={`text-xs font-medium rounded-full px-3 py-1 ${
                          note.madeGoal
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}>
                          {note.madeGoal ? 'Goal' : `${Math.round(note.taskCompletionPct ?? 0)}%`}
                        </span>
                      )}
                      <a
                        href={`${ALBATROSS_BASE}/competitions/${encodeURIComponent(albatrossCompId)}/tasks/${encodeURIComponent(note.taskId)}/flights/${encodeURIComponent(note.id)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-fwo-blue hover:text-blue-700 hover:underline px-2 py-1"
                        title="Open flight on Albatross"
                      >
                        Flight <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap mb-4">
                    {note.narrative}
                  </p>
                  {(note.strengths.length > 0 || note.improvements.length > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {note.strengths.length > 0 && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                          <p className="text-xs font-semibold text-emerald-800 mb-1.5 uppercase tracking-wide">Strengths</p>
                          <ul className="space-y-1 text-sm text-emerald-900">
                            {note.strengths.map((s, i) => <li key={i}>• {s}</li>)}
                          </ul>
                        </div>
                      )}
                      {note.improvements.length > 0 && (
                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                          <p className="text-xs font-semibold text-amber-800 mb-1.5 uppercase tracking-wide">Areas to work on</p>
                          <ul className="space-y-1 text-sm text-amber-900">
                            {note.improvements.map((s, i) => <li key={i}>• {s}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {note.recommendations.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Recommendations</p>
                      {note.recommendations.map((r, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start gap-2 mb-1">
                            <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 mt-0.5 ${
                              r.priority === 'high' ? 'bg-red-100 text-red-700' :
                              r.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{r.priority}</span>
                            <p className="font-medium text-sm text-gray-900">{r.title}</p>
                          </div>
                          <p className="text-sm text-gray-600 ml-1">{r.detail}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </ProtectedRoute>
  )
}
